import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { GitBranch, Pencil, Trash2 } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { formatDate } from "@/lib/format"
import { toast } from "sonner"

const API = API_BASE

function buildActions(form) {
  if (form.actionType === "create_task") {
    return [
      {
        type: "create_task",
        title: form.taskTitle,
        description: form.taskDescription,
      },
    ]
  }
  return [
    {
      type: "log_communication",
      channel: form.channel,
      subject: form.subject,
      body: form.body,
    },
  ]
}

function formFromWorkflow(w) {
  const action = (w.actions || [])[0] || {}
  if (action.type === "create_task") {
    return {
      name: w.name || "",
      trigger_event: w.trigger_event || "appointment.completed",
      actionType: "create_task",
      channel: "email",
      subject: "",
      body: "",
      taskTitle: action.title || "",
      taskDescription: action.description || "",
    }
  }
  return {
    name: w.name || "",
    trigger_event: w.trigger_event || "appointment.completed",
    actionType: "log_communication",
    channel: action.channel || "email",
    subject: action.subject || "",
    body: action.body || "",
    taskTitle: "Urmărire post-vizită",
    taskDescription: "Generat automat",
  }
}

const defaultForm = () => ({
  name: "",
  trigger_event: "appointment.completed",
  actionType: "log_communication",
  channel: "email",
  subject: "Mulțumim pentru vizită",
  body: "Bună ziua {{client_name}}, vă mulțumim pentru vizita de azi.",
  taskTitle: "Urmărire post-vizită",
  taskDescription: "Generat automat pentru {{client_name}}",
})

export default function Workflows() {
  const { t } = useTranslation()
  const [workflows, setWorkflows] = useState([])
  const [summary, setSummary] = useState({ active_count: 0, total_runs: 0 })
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(defaultForm())
  const [editingId, setEditingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [wfRes, runsRes] = await Promise.all([
        axios.get(`${API}/workflows`),
        axios.get(`${API}/workflow-runs`, { params: { limit: 30 } }),
      ])
      const payload = wfRes.data
      if (Array.isArray(payload)) {
        setWorkflows(payload)
        setSummary({ active_count: payload.filter((w) => w.active).length, total_runs: 0 })
      } else {
        setWorkflows(payload.workflows || [])
        setSummary(payload.summary || { active_count: 0, total_runs: 0 })
      }
      setRuns(runsRes.data || [])
    } catch {
      toast.error(t("app.workflows.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setForm(defaultForm())
    setEditingId(null)
  }

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("app.workflows.toast.nameRequired"))
    const body = {
      name: form.name.trim(),
      trigger_event: form.trigger_event,
      actions: buildActions(form),
      active: true,
    }
    try {
      if (editingId) {
        await axios.patch(`${API}/workflows/${editingId}`, body)
        toast.success(t("app.workflows.toast.updated"))
      } else {
        await axios.post(`${API}/workflows`, body)
        toast.success(t("app.workflows.toast.created"))
      }
      resetForm()
      load()
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.workflows.toast.saveFailed"))
    }
  }

  const toggleActive = async (w) => {
    try {
      await axios.patch(`${API}/workflows/${w.id}`, { active: !w.active })
      load()
    } catch {
      toast.error(t("app.workflows.toast.saveFailed"))
    }
  }

  const startEdit = (w) => {
    setEditingId(w.id)
    setForm(formFromWorkflow(w))
  }

  const deleteWorkflow = async (id) => {
    try {
      await axios.delete(`${API}/workflows/${id}`)
      if (editingId === id) resetForm()
      toast.success(t("app.workflows.toast.deleted"))
      load()
    } catch {
      toast.error(t("app.workflows.toast.saveFailed"))
    }
  }

  const triggerLabel = (value) => {
    if (value === "appointment.completed") return t("app.workflows.triggers.appointmentCompleted")
    if (value === "queue.ticket_called") return t("app.workflows.triggers.ticketCalled")
    if (value === "admission.created") return t("app.workflows.triggers.admissionCreated")
    return value
  }

  const actionLabel = (actions) => {
    const a = (actions || [])[0]
    if (!a) return "—"
    if (a.type === "create_task") return t("app.workflows.actions.createTask")
    return t("app.workflows.actions.logCommunication")
  }

  return (
    <FeatureGate
      feature="workflows"
      title={t("app.workflows.gateTitle")}
      description={t("app.workflows.gateDescription")}
    >
      <AppLayout title={t("app.workflows.title")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.workflows.intro")}{" "}
          <Link to="/app/communications" className="underline underline-offset-2">
            {t("app.nav.communications")}
          </Link>
          {" · "}
          <Link to="/app/appointments" className="underline underline-offset-2">
            {t("app.nav.appointments")}
          </Link>
        </p>

        <PilotBanner className="mb-4 max-w-2xl">{t("app.workflows.pilotHint")}</PilotBanner>

        <div className="grid sm:grid-cols-2 gap-3 mb-6 max-w-md">
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.workflows.metrics.active")}</p>
            <p className="font-display text-2xl mt-1">{summary.active_count}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.workflows.metrics.runs")}</p>
            <p className="font-display text-2xl mt-1">{summary.total_runs}</p>
          </article>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <section className="rounded-xl border border-border p-6 bg-card space-y-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <GitBranch className="h-4 w-4" />
              {editingId ? t("app.workflows.editFlow") : t("app.workflows.newFlow")}
            </h3>

            <div>
              <Label className="text-xs">{t("app.workflows.fieldName")}</Label>
              <Input
                className="mt-1"
                placeholder={t("app.workflows.namePlaceholder")}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div>
              <Label className="text-xs">{t("app.workflows.fieldTrigger")}</Label>
              <Select
                value={form.trigger_event}
                onValueChange={(v) => setForm((f) => ({ ...f, trigger_event: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appointment.completed">
                    {t("app.workflows.triggers.appointmentCompleted")}
                  </SelectItem>
                  <SelectItem value="queue.ticket_called">
                    {t("app.workflows.triggers.ticketCalled")}
                  </SelectItem>
                  <SelectItem value="admission.created">
                    {t("app.workflows.triggers.admissionCreated")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">{t("app.workflows.fieldAction")}</Label>
              <Select
                value={form.actionType}
                onValueChange={(v) => setForm((f) => ({ ...f, actionType: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="log_communication">
                    {t("app.workflows.actions.logCommunication")}
                  </SelectItem>
                  <SelectItem value="create_task">{t("app.workflows.actions.createTask")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.actionType === "log_communication" ? (
              <>
                <div>
                  <Label className="text-xs">{t("app.workflows.fieldChannel")}</Label>
                  <Select
                    value={form.channel}
                    onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="phone">{t("app.workflows.channelPhone")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("app.workflows.fieldSubject")}</Label>
                  <Input
                    className="mt-1"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("app.workflows.fieldBody")}</Label>
                  <Textarea
                    className="mt-1 min-h-[80px]"
                    value={form.body}
                    onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-xs">{t("app.workflows.fieldTaskTitle")}</Label>
                  <Input
                    className="mt-1"
                    value={form.taskTitle}
                    onChange={(e) => setForm((f) => ({ ...f, taskTitle: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("app.workflows.fieldTaskDescription")}</Label>
                  <Textarea
                    className="mt-1 min-h-[80px]"
                    value={form.taskDescription}
                    onChange={(e) => setForm((f) => ({ ...f, taskDescription: e.target.value }))}
                  />
                </div>
              </>
            )}

            <p className="text-xs text-muted-foreground rounded-lg bg-muted/50 px-3 py-2">
              {t("app.workflows.templateHint")}{" "}
              <code className="text-[11px]">{"{{client_name}}"}</code>,{" "}
              <code className="text-[11px]">{"{{appointment_id}}"}</code>
            </p>

            <div className="flex flex-wrap gap-2">
              <Button className="rounded-full" onClick={save}>
                {editingId ? t("app.workflows.save") : t("app.workflows.createBtn")}
              </Button>
              {editingId && (
                <Button variant="ghost" className="rounded-full" onClick={resetForm}>
                  {t("app.workflows.cancel")}
                </Button>
              )}
            </div>
          </section>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-2">{t("app.workflows.listTitle")}</h3>
              <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
                {loading && (
                  <li className="px-4 py-8 text-center text-muted-foreground">
                    {t("app.workflows.loading")}
                  </li>
                )}
                {!loading &&
                  workflows.map((w) => (
                    <li key={w.id} className="px-4 py-3 space-y-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <button
                          type="button"
                          className="text-left flex-1 min-w-0 hover:opacity-80"
                          onClick={() => startEdit(w)}
                        >
                          <p className="font-medium flex items-center gap-2 flex-wrap">
                            {w.name}
                            {!w.active && (
                              <Badge variant="secondary" className="text-[10px]">
                                {t("app.workflows.paused")}
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {triggerLabel(w.trigger_event)} → {actionLabel(w.actions)}
                          </p>
                        </button>
                        <div className="flex items-center gap-1 shrink-0">
                          <Switch checked={!!w.active} onCheckedChange={() => toggleActive(w)} />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => startEdit(w)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("app.workflows.deleteTitle")}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("app.workflows.deleteBody")}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("app.workflows.cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteWorkflow(w.id)}>
                                  {t("app.workflows.confirmDelete")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </li>
                  ))}
                {!loading && !workflows.length && (
                  <li className="px-4 py-8 text-center text-muted-foreground">
                    {t("app.workflows.empty")}
                  </li>
                )}
              </ul>
            </div>

            <section>
              <h3 className="text-sm font-medium mb-2">{t("app.workflows.historyTitle")}</h3>
              <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm max-h-[320px] overflow-auto">
                {runs.map((r) => {
                  let results = r.results || []
                  if (typeof results === "string") {
                    try {
                      results = JSON.parse(results)
                    } catch {
                      results = []
                    }
                  }
                  if (!Array.isArray(results)) results = []
                  return (
                    <li key={r.id} className="px-4 py-3">
                      <p className="font-medium">{r.workflow_name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {triggerLabel(r.trigger_event)} · {formatDate(r.created_at)}
                        {r.status === "skipped" && ` · ${t("app.workflows.runSkipped")}`}
                      </p>
                      {results.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {results.map((x) => x.type).join(", ")}
                        </p>
                      )}
                    </li>
                  )
                })}
                {!runs.length && (
                  <li className="px-4 py-8 text-center text-muted-foreground">
                    {t("app.workflows.historyEmpty")}
                  </li>
                )}
              </ul>
            </section>
          </div>
        </div>
      </AppLayout>
    </FeatureGate>
  )
}
